import {
  Check,
  CheckSquare,
  ExternalLink,
  Info,
  Link,
  Mail,
  MapPin,
  Sparkles,
} from 'lucide-react';
import {
  Badge,
  ProfileBadge,
  AvatarGroup,
  TaskEventDateIcon,
} from '@/components/ui';
import { formatDueDate } from '@/lib/date';

export interface ChildProfile {
  name: string;
  emoji?: string;
  color?: string;
  avatarSrc?: string;
  imagePath?: string | null;
}

/**
 * Household adult (member) assigned to the task. Rendered as the
 * bottom-right overlapping avatar cluster — distinct from `children`,
 * which render as the labelled chip row.
 */
export interface MemberProfile {
  name: string;
  emoji?: string;
  color?: string;
  imagePath?: string | null;
}

export interface TaskCardProps {
  id: string;
  title: string;
  description?: string;
  dueDate?: string;
  location?: string;
  link?: string;
  status: 'TODO' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED';
  children?: ChildProfile[];
  /** Household adults assigned to the task (the avatar cluster). */
  members?: MemberProfile[];
  /**
   * Subject of the originating email when the task was committed from
   * the review pipeline. Rendered as a compact "From: …" line to show
   * traceability without wiring a full attribution panel.
   */
  sourceSubject?: string;
  category?: string;
  isOverdue?: boolean;
  relatedEventTitle?: string;
  onToggleComplete?: () => void;
  /** Tapping the card body (outside the checkbox / link) opens edit. */
  onClick?: () => void;
  /** IANA timezone for the due-date display. Defaults to browser TZ. */
  timezone?: string | null;
  /**
   * Visual variant. `"review"` swaps the round toggle checkbox for a small
   * icon tile with a sparkles overlay to signal AI-extracted items inside
   * the review flow.
   */
  variant?: 'default' | 'review';
  /**
   * Label rendered in the date slot when `dueDate` is undefined.
   * Today's "No date" section sets this to "Date TBD" so missing-date
   * tasks stay discoverable; other contexts leave it unset so the card
   * silently omits the date row.
   */
  noDateLabel?: string;
  /** Dim the card — used for past days in the Schedule feed (matches EventCard). */
  muted?: boolean;
}

export function TaskCard({
  title,
  description,
  dueDate,
  location,
  link,
  status,
  children = [],
  members = [],
  sourceSubject,
  category,
  isOverdue = false,
  relatedEventTitle,
  onToggleComplete,
  onClick,
  timezone,
  variant = 'default',
  noDateLabel,
  muted = false,
}: TaskCardProps) {
  const isCompleted = status === 'DONE' || status === 'CANCELLED';
  const isClickable = !!onClick;
  const isReview = variant === 'review';

  return (
    <div
      className={[
        'bg-card border border-border-strong rounded-lg p-5',
        muted || isCompleted ? 'opacity-60' : '',
        isClickable ? 'cursor-pointer' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="flex items-start gap-3">
        {isReview ? (
          <div className="relative shrink-0">
            <div className="w-10 h-10 rounded-lg bg-card border border-border flex items-center justify-center">
              <CheckSquare
                size={18}
                className="text-primary"
              />
            </div>
            <Sparkles
              size={14}
              className="absolute -top-1.5 -left-1.5 text-foreground"
              aria-label="AI-extracted"
            />
          </div>
        ) : (
          <div className="pt-1 shrink-0">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleComplete?.();
              }}
              className={[
                'w-5 h-5 rounded-full border inline-flex items-center justify-center cursor-pointer',
                isCompleted
                  ? // History/past (muted) de-emphasizes the completed state to
                    // gray (#f5f5f5) per YAR-131/YAR-257; active views keep the
                    // green fill that reads as a fresh "done".
                    muted
                    ? 'bg-surface-muted border-border-strong'
                    : 'bg-primary border-primary'
                  : isOverdue
                    ? 'bg-danger-light border-danger'
                    : 'bg-background border-border',
              ].join(' ')}
              aria-label={isCompleted ? 'Mark incomplete' : 'Mark complete'}
            >
              {isCompleted && (
                <Check
                  size={12}
                  className={
                    // #7a7a7a (text/placeholder) when muted, matching the
                    // detail modal's ring — YAR-257.
                    muted ? 'text-text-placeholder' : 'text-primary-foreground'
                  }
                />
              )}
            </button>
          </div>
        )}

        <div className="flex-1 min-w-0">
          <p
            className={[
              'text-base font-semibold text-text-body',
              isCompleted ? 'line-through' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {title}
          </p>

          {description && (
            <p className="text-sm text-muted-foreground mt-1 break-words">
              {description}
            </p>
          )}

          {dueDate && (
            <div className="flex items-center gap-1.5 mt-2">
              <TaskEventDateIcon
                size={16}
                className={isOverdue ? 'text-danger' : 'text-muted-foreground'}
              />
              <span
                className={[
                  'text-sm',
                  isOverdue
                    ? 'text-danger font-medium'
                    : 'text-muted-foreground',
                ].join(' ')}
              >
                {formatDueDate(dueDate, timezone)}
              </span>
            </div>
          )}

          {!dueDate && noDateLabel && (
            <div className="flex items-center gap-1.5 mt-2">
              <TaskEventDateIcon
                size={16}
                className="text-muted-foreground"
              />
              <span className="text-sm italic text-muted-foreground">
                {noDateLabel}
              </span>
            </div>
          )}

          {location && (
            <div className="flex items-center gap-1.5 mt-2">
              <MapPin
                size={14}
                className="text-muted-foreground"
              />
              <span className="text-sm text-muted-foreground">{location}</span>
            </div>
          )}

          {link && (
            <div className="flex items-center gap-1.5 mt-2">
              <Link
                size={14}
                className="text-muted-foreground"
              />
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 min-w-0 text-sm text-primary hover:text-primary-hover underline-offset-2 hover:underline truncate"
              >
                <span className="truncate">{link}</span>
                <ExternalLink
                  size={12}
                  className="shrink-0"
                />
              </a>
            </div>
          )}

          {relatedEventTitle && (
            <div className="flex items-center gap-1.5 mt-2">
              <Info
                size={14}
                className="text-info"
              />
              <span className="text-xs text-info">
                Related to: {relatedEventTitle}
              </span>
            </div>
          )}

          {sourceSubject && (
            <div className="flex items-center gap-1.5 mt-2 min-w-0">
              <Mail
                size={14}
                className="text-muted-foreground shrink-0"
              />
              <span className="text-xs text-muted-foreground truncate">
                From: {sourceSubject}
              </span>
            </div>
          )}

          {(children.length > 0 || members.length > 0 || category) && (
            <div className="flex items-center justify-between gap-2 mt-3">
              <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                {children.slice(0, 2).map((child) => (
                  <ProfileBadge
                    key={child.name}
                    name={child.name}
                    emoji={child.emoji}
                    color={child.color}
                    imagePath={child.imagePath}
                  />
                ))}
                {children.length > 2 && (
                  <span className="inline-flex items-center rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                    +{children.length - 2}
                  </span>
                )}
                {category && (
                  <Badge
                    variant="info"
                    size="sm"
                  >
                    {category}
                  </Badge>
                )}
              </div>
              {members.length > 0 && (
                <AvatarGroup
                  className="shrink-0"
                  avatars={members.map((m) => ({
                    emoji: m.emoji,
                    color: m.color,
                    imagePath: m.imagePath,
                    alt: m.name,
                  }))}
                  max={3}
                  size="sm"
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
